import { collection, query, where, orderBy, limit, getDocs, getDoc, doc, setDoc, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { EntityIntakeDefaults } from './entityFormationDefaults';

/**
 * Draft document structure in Firestore
 */
export interface EntityFormationDraft {
  createdByUid: string;
  createdByEmail: string;
  status: 'draft';
  version: number;
  data: EntityIntakeDefaults;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

/**
 * Loads the latest entity formation draft for a user
 * @param uid - The user ID
 * @returns The latest draft with draftId, data, and updatedAt timestamp, or null if no draft exists
 */
export async function loadLatestEntityFormationDraft(uid: string): Promise<{ draftId: string; data: EntityIntakeDefaults; updatedAt: Timestamp | null } | null> {
  const draftsRef = collection(db, 'intakeEntityFormationDrafts');
  const q = query(
    draftsRef,
    where('createdByUid', '==', uid),
    orderBy('updatedAt', 'desc'),
    limit(1)
  );

  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return null;
  }

  const docSnapshot = querySnapshot.docs[0];
  const docData = docSnapshot.data();
  return {
    draftId: docSnapshot.id,
    data: docData.data as EntityIntakeDefaults,
    updatedAt: docData.updatedAt as Timestamp | null,
  };
}

/**
 * Loads a specific entity formation draft by ID
 * @param draftId - The draft document ID
 * @returns The draft with draftId, data, and timestamps, or null if not found or access denied
 */
export async function loadEntityFormationDraftById(draftId: string): Promise<{ draftId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null } | null> {
  const draftDocRef = doc(db, 'intakeEntityFormationDrafts', draftId);
  const docSnapshot = await getDoc(draftDocRef);
  
  if (!docSnapshot.exists()) {
    return null;
  }

  const docData = docSnapshot.data() as EntityFormationDraft;
  return {
    draftId: docSnapshot.id,
    data: docData.data,
    createdAt: docData.createdAt,
    updatedAt: docData.updatedAt,
  };
}

/**
 * Loads all entity formation drafts for a user
 * @param uid - The user ID
 * @returns Array of drafts with their IDs and metadata
 */
export async function loadAllEntityFormationDrafts(uid: string): Promise<Array<{ draftId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null }>> {
  const draftsRef = collection(db, 'intakeEntityFormationDrafts');
  const q = query(
    draftsRef,
    where('createdByUid', '==', uid),
    orderBy('updatedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const drafts: Array<{ draftId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null }> = [];
  
  querySnapshot.forEach((docSnapshot) => {
    const docData = docSnapshot.data() as EntityFormationDraft;
    drafts.push({
      draftId: docSnapshot.id,
      data: docData.data,
      createdAt: docData.createdAt,
      updatedAt: docData.updatedAt,
    });
  });

  return drafts;
}

/**
 * Loads all entity formation drafts (admin only)
 * @returns Array of all drafts with their IDs, metadata, and user info
 */
export async function loadAllEntityFormationDraftsAdmin(): Promise<Array<{ draftId: string; createdByUid: string; createdByEmail: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null }>> {
  const draftsRef = collection(db, 'intakeEntityFormationDrafts');
  const q = query(
    draftsRef,
    orderBy('updatedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const drafts: Array<{ draftId: string; createdByUid: string; createdByEmail: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null }> = [];
  
  querySnapshot.forEach((docSnapshot) => {
    const docData = docSnapshot.data() as EntityFormationDraft;
    drafts.push({
      draftId: docSnapshot.id,
      createdByUid: docData.createdByUid,
      createdByEmail: docData.createdByEmail,
      data: docData.data,
      createdAt: docData.createdAt,
      updatedAt: docData.updatedAt,
    });
  });

  return drafts;
}

/**
 * Saves or updates an entity formation draft (upsert)
 * @param args - Save parameters including uid, email, optional draftId, and data
 * @returns The document ID of the saved draft and the updatedAt timestamp
 */
export async function saveEntityFormationDraft(args: { uid: string; email: string; draftId?: string; data: EntityIntakeDefaults }): Promise<{ draftId: string; updatedAt: Timestamp | null }> {
  const { uid, email, draftId, data } = args;

  if (draftId) {
    // Update existing draft (upsert with merge)
    const draftDocRef = doc(db, 'intakeEntityFormationDrafts', draftId);
    await setDoc(draftDocRef, {
      createdByUid: uid,
      createdByEmail: email,
      status: 'draft',
      version: 1,
      data,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    // Fetch the document to get the server timestamp
    const updatedDoc = await getDoc(draftDocRef);
    const updatedData = updatedDoc.data();
    return { 
      draftId,
      updatedAt: updatedData?.updatedAt as Timestamp | null ?? null
    };
  } else {
    // Create new draft
    const draftsRef = collection(db, 'intakeEntityFormationDrafts');
    const docRef = await addDoc(draftsRef, {
      createdByUid: uid,
      createdByEmail: email,
      status: 'draft',
      version: 1,
      data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Fetch the document to get the server timestamp
    const createdDoc = await getDoc(docRef);
    const createdData = createdDoc.data();
    return { 
      draftId: docRef.id,
      updatedAt: createdData?.updatedAt as Timestamp | null ?? null
    };
  }
}

